import { createServerFn } from "@tanstack/react-start";
import { and, eq, inArray, notInArray } from "drizzle-orm";
import { z } from "zod";
import { db } from "~/lib/db";
import {
  canonicalReferences,
  citationSourceLinks,
  importBatches,
  projectCitations,
  sourceRecords,
} from "~/lib/db/schema";
import { detectDuplicatesForProject } from "~/lib/duplicates/detection";
import { requireProjectAccess, requireUser } from "~/lib/server/helpers.server";

const getImportBatchesSchema = z.object({
  projectId: z.string(),
});

const undoImportBatchSchema = z.object({
  batchId: z.string(),
});

export const $getImportBatches = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => getImportBatchesSchema.parse(data))
  .handler(async ({ data }) => {
    const user = await requireUser();
    await requireProjectAccess(data.projectId, user.id);

    const batches = await db.query.importBatches.findMany({
      where: and(
        eq(importBatches.projectId, data.projectId),
        eq(importBatches.status, "completed"),
      ),
      orderBy: (ib, { desc }) => [desc(ib.createdAt)],
      with: {
        importedByUser: true,
      },
    });

    return batches.map((batch) => ({
      id: batch.id,
      projectId: batch.projectId,
      sourceType: batch.sourceType,
      sourceName: batch.sourceName,
      filename: batch.filename,
      recordsTotal: batch.recordsTotal,
      recordsImported: batch.recordsImported,
      recordsDuplicated: batch.recordsDuplicated,
      recordsFailed: batch.recordsFailed,
      status: batch.status,
      createdAt: batch.createdAt,
      completedAt: batch.completedAt,
      importedBy: {
        name: batch.importedByUser.name,
        image: batch.importedByUser.image,
      },
    }));
  });

export const $undoImportBatch = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => undoImportBatchSchema.parse(data))
  .handler(async ({ data }) => {
    const user = await requireUser();

    // Get the batch and verify ownership
    const batch = await db.query.importBatches.findFirst({
      where: eq(importBatches.id, data.batchId),
    });

    if (!batch) {
      throw new Error("Import batch not found");
    }

    await requireProjectAccess(batch.projectId, user.id);

    // Get all source records for this batch
    const batchSourceRecords = await db.query.sourceRecords.findMany({
      where: eq(sourceRecords.importBatchId, data.batchId),
    });

    const sourceRecordIds = batchSourceRecords.map((sr) => sr.id);

    // Get all citation source links for these source records
    const linksToDelete =
      sourceRecordIds.length > 0
        ? await db.query.citationSourceLinks.findMany({
            where: inArray(citationSourceLinks.sourceRecordId, sourceRecordIds),
          })
        : [];

    // Get unique canonical reference IDs from these links
    const canonicalRefIds = [
      ...new Set(linksToDelete.map((l) => l.canonicalReferenceId)),
    ];

    // For each canonical reference, check if it has other source links
    // If not, we should delete it and its project citation
    const orphanedCanonicalRefIds: string[] = [];

    for (const refId of canonicalRefIds) {
      const otherLinks = await db.query.citationSourceLinks.findMany({
        where: and(
          eq(citationSourceLinks.canonicalReferenceId, refId),
          sourceRecordIds.length > 0
            ? notInArray(citationSourceLinks.sourceRecordId, sourceRecordIds)
            : undefined,
        ),
      });

      if (otherLinks.length === 0) {
        orphanedCanonicalRefIds.push(refId);
      }
    }

    // Delete in order to respect foreign key constraints:
    // 1. Delete citation source links (the ones associated with this batch's source records)
    if (linksToDelete.length > 0) {
      await db.delete(citationSourceLinks).where(
        inArray(
          citationSourceLinks.id,
          linksToDelete.map((l) => l.id),
        ),
      );
    }

    // 2. Delete source records (cascade from import batch will handle this, but let's be explicit)
    if (sourceRecordIds.length > 0) {
      await db.delete(sourceRecords).where(inArray(sourceRecords.id, sourceRecordIds));
    }

    // 3. Delete project citations for orphaned canonical references
    if (orphanedCanonicalRefIds.length > 0) {
      await db
        .delete(projectCitations)
        .where(
          and(
            eq(projectCitations.projectId, batch.projectId),
            inArray(projectCitations.canonicalReferenceId, orphanedCanonicalRefIds),
          ),
        );

      // 4. Delete orphaned canonical references
      await db
        .delete(canonicalReferences)
        .where(inArray(canonicalReferences.id, orphanedCanonicalRefIds));
    }

    // 5. Delete the import batch
    await db.delete(importBatches).where(eq(importBatches.id, data.batchId));

    // 6. Re-run duplicate detection to update any affected groups
    await detectDuplicatesForProject(batch.projectId);

    return {
      deletedBatchId: data.batchId,
      deletedSourceRecords: sourceRecordIds.length,
      deletedCanonicalReferences: orphanedCanonicalRefIds.length,
    };
  });
